// personalizationService.ts - Deep Personalization Engine for Cipher
// Tracks user behavior and personalizes the entire experience

export interface TickerView {
  ticker: string;
  view_count: number;
  last_viewed: string;
  sector?: string;
}

export interface FeatureUsage {
  feature: string;
  use_count: number;
  last_used: string;
}

export interface UserProfile {
  most_viewed_tickers: TickerView[];
  most_used_features: FeatureUsage[];
  preferred_chart_interval: string;
  preferred_screener_filters: Record<string, any>;
  session_count: number;
  first_session_date: string;
  last_session_date: string;
  total_time_in_app_minutes: number;
  investment_style_inferred: 'momentum' | 'value' | 'growth' | 'income' | 'unknown';
  sectors_of_interest: string[];
  streak_days: number;
  last_streak_date: string;
  dismissed_suggestions: string[];
  achievements_earned: string[];
  onboarding_completed: boolean;
  onboarding_step: number;
}

const STORAGE_KEY = 'apex_user_profile';
const SESSION_START_KEY = 'apex_session_start';

// Sector mapping for common tickers - infer sector from viewed stocks
const TICKER_SECTORS: Record<string, string> = {
  AAPL: 'Technology', MSFT: 'Technology', GOOGL: 'Technology', GOOG: 'Technology',
  AMZN: 'Consumer Cyclical', META: 'Technology', NVDA: 'Technology', AMD: 'Technology',
  TSLA: 'Consumer Cyclical', NFLX: 'Communication Services', CRM: 'Technology',
  ORCL: 'Technology', ADBE: 'Technology', INTC: 'Technology', CSCO: 'Technology',
  JPM: 'Financial Services', BAC: 'Financial Services', WFC: 'Financial Services',
  GS: 'Financial Services', MS: 'Financial Services', C: 'Financial Services',
  JNJ: 'Healthcare', UNH: 'Healthcare', PFE: 'Healthcare', ABBV: 'Healthcare',
  MRK: 'Healthcare', LLY: 'Healthcare', BMY: 'Healthcare', AMGN: 'Healthcare',
  XOM: 'Energy', CVX: 'Energy', COP: 'Energy', SLB: 'Energy', EOG: 'Energy',
  PG: 'Consumer Defensive', KO: 'Consumer Defensive', PEP: 'Consumer Defensive',
  WMT: 'Consumer Defensive', COST: 'Consumer Defensive', TGT: 'Consumer Cyclical',
  HD: 'Consumer Cyclical', LOW: 'Consumer Cyclical', NKE: 'Consumer Cyclical',
  DIS: 'Communication Services', CMCSA: 'Communication Services', VZ: 'Communication Services',
  T: 'Communication Services', TMUS: 'Communication Services',
  BA: 'Industrials', CAT: 'Industrials', HON: 'Industrials', UPS: 'Industrials',
  LMT: 'Industrials', RTX: 'Industrials', GE: 'Industrials', MMM: 'Industrials',
  NEE: 'Utilities', DUK: 'Utilities', SO: 'Utilities', D: 'Utilities',
  AMT: 'Real Estate', PLD: 'Real Estate', CCI: 'Real Estate', SPG: 'Real Estate',
  AGX: 'Industrials', // Argan Inc - construction/engineering
};

// Investment style inference based on viewed tickers
const TICKER_STYLES: Record<string, string[]> = {
  // Momentum stocks
  NVDA: ['momentum', 'growth'], AMD: ['momentum', 'growth'], TSLA: ['momentum', 'growth'],
  META: ['momentum', 'growth'], NFLX: ['momentum', 'growth'], PLTR: ['momentum', 'growth'],
  // Value stocks
  JPM: ['value'], BAC: ['value'], WFC: ['value'], BRK: ['value'],
  JNJ: ['value', 'income'], PG: ['value', 'income'], KO: ['value', 'income'],
  // Growth stocks
  AMZN: ['growth'], GOOGL: ['growth'], CRM: ['growth'], SHOP: ['growth'],
  SQ: ['growth'], PYPL: ['growth'], UBER: ['growth'],
  // Income/Dividend stocks
  VZ: ['income'], T: ['income'], XOM: ['income'], CVX: ['income'],
  PFE: ['income'], ABBV: ['income'], MO: ['income'], PM: ['income'],
};

function getDefaultProfile(): UserProfile {
  return {
    most_viewed_tickers: [],
    most_used_features: [],
    preferred_chart_interval: '1Y',
    preferred_screener_filters: {},
    session_count: 0,
    first_session_date: '',
    last_session_date: '',
    total_time_in_app_minutes: 0,
    investment_style_inferred: 'unknown',
    sectors_of_interest: [],
    streak_days: 0,
    last_streak_date: '',
    dismissed_suggestions: [],
    achievements_earned: [],
    onboarding_completed: false,
    onboarding_step: 0,
  };
}

export function loadProfile(): UserProfile {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...getDefaultProfile(), ...JSON.parse(stored) };
    }
  } catch (e) {
    console.warn('Failed to load user profile:', e);
  }
  return getDefaultProfile();
}

export function saveProfile(profile: UserProfile): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch (e) {
    console.warn('Failed to save user profile:', e);
  }
}

// Start a new session - call this on app mount
export function startSession(): UserProfile {
  const profile = loadProfile();
  const now = new Date();
  const today = now.toISOString().split('T')[0];

  // Check if this is a new day
  const lastDate = profile.last_session_date?.split('T')[0];
  const isNewDay = lastDate !== today;

  if (isNewDay) {
    profile.session_count += 1;

    // Update streak
    if (lastDate) {
      const lastDateObj = new Date(lastDate);
      const diffDays = Math.floor((now.getTime() - lastDateObj.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 1) {
        // Consecutive day
        profile.streak_days += 1;
      } else if (diffDays > 1) {
        // Streak broken
        profile.streak_days = 1;
      }
    } else {
      profile.streak_days = 1;
    }
    profile.last_streak_date = today;
  }

  if (!profile.first_session_date) {
    profile.first_session_date = now.toISOString();
  }
  profile.last_session_date = now.toISOString();

  // Track session start time for duration calculation
  sessionStorage.setItem(SESSION_START_KEY, now.toISOString());

  saveProfile(profile);
  return profile;
}

// End session - call this on app unmount or before unload
export function endSession(): void {
  const profile = loadProfile();
  const startTime = sessionStorage.getItem(SESSION_START_KEY);

  if (startTime) {
    const duration = (Date.now() - new Date(startTime).getTime()) / (1000 * 60);
    profile.total_time_in_app_minutes += Math.round(duration);
    saveProfile(profile);
  }
}

// Track a stock view
export function trackTickerView(ticker: string, sector?: string): void {
  const profile = loadProfile();
  const existing = profile.most_viewed_tickers.find(t => t.ticker === ticker);

  const inferredSector = sector || TICKER_SECTORS[ticker.toUpperCase()];

  if (existing) {
    existing.view_count += 1;
    existing.last_viewed = new Date().toISOString();
    if (inferredSector) existing.sector = inferredSector;
  } else {
    profile.most_viewed_tickers.push({
      ticker: ticker.toUpperCase(),
      view_count: 1,
      last_viewed: new Date().toISOString(),
      sector: inferredSector,
    });
  }

  // Sort by view count descending
  profile.most_viewed_tickers.sort((a, b) => b.view_count - a.view_count);

  // Keep top 50
  profile.most_viewed_tickers = profile.most_viewed_tickers.slice(0, 50);

  // Update sectors of interest
  updateSectorsOfInterest(profile);

  // Update investment style inference
  updateInvestmentStyle(profile);

  saveProfile(profile);
}

// Track feature usage
export function trackFeatureUsage(feature: string): void {
  const profile = loadProfile();
  const existing = profile.most_used_features.find(f => f.feature === feature);

  if (existing) {
    existing.use_count += 1;
    existing.last_used = new Date().toISOString();
  } else {
    profile.most_used_features.push({
      feature,
      use_count: 1,
      last_used: new Date().toISOString(),
    });
  }

  // Sort by use count descending
  profile.most_used_features.sort((a, b) => b.use_count - a.use_count);

  saveProfile(profile);
}

// Track chart interval preference
export function trackChartInterval(interval: string): void {
  const profile = loadProfile();
  profile.preferred_chart_interval = interval;
  saveProfile(profile);
}

// Track screener filters
export function trackScreenerFilters(filters: Record<string, any>): void {
  const profile = loadProfile();
  profile.preferred_screener_filters = filters;
  saveProfile(profile);
}

// Update sectors of interest based on viewed tickers
function updateSectorsOfInterest(profile: UserProfile): void {
  const sectorCounts: Record<string, number> = {};

  for (const tv of profile.most_viewed_tickers) {
    if (tv.sector) {
      sectorCounts[tv.sector] = (sectorCounts[tv.sector] || 0) + tv.view_count;
    }
  }

  profile.sectors_of_interest = Object.entries(sectorCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([sector]) => sector);
}

// Infer investment style from viewed tickers
function updateInvestmentStyle(profile: UserProfile): void {
  const styleCounts: Record<string, number> = {
    momentum: 0,
    value: 0,
    growth: 0,
    income: 0,
  };

  for (const tv of profile.most_viewed_tickers) {
    const styles = TICKER_STYLES[tv.ticker.toUpperCase()];
    if (styles) {
      for (const style of styles) {
        styleCounts[style] += tv.view_count;
      }
    }
  }

  const topStyle = Object.entries(styleCounts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])[0];

  if (topStyle && topStyle[1] >= 3) {
    profile.investment_style_inferred = topStyle[0] as UserProfile['investment_style_inferred'];
  }
}

// Get personalized greeting based on session count and time
export function getPersonalizedGreeting(profile: UserProfile): string {
  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';

  if (profile.session_count <= 3) {
    return "Welcome to Cipher. Here's your market intelligence.";
  } else if (profile.session_count <= 10) {
    return "Welcome back. Here's what changed since your last visit.";
  } else {
    return `Good ${timeOfDay}, Justin. Markets have moved since yesterday.`;
  }
}

// Check if user should see watchlist suggestion for a ticker
export function shouldSuggestWatchlist(ticker: string, watchlist: string[]): boolean {
  const profile = loadProfile();
  const viewed = profile.most_viewed_tickers.find(t => t.ticker === ticker.toUpperCase());

  if (!viewed) return false;
  if (watchlist.includes(ticker.toUpperCase())) return false;

  return viewed.view_count >= 5;
}

// Get suggested tickers based on user preferences
export function getSuggestedTickers(currentWatchlist: string[]): string[] {
  const profile = loadProfile();

  // Get frequently viewed but not in watchlist
  const suggestions = profile.most_viewed_tickers
    .filter(t => !currentWatchlist.includes(t.ticker))
    .filter(t => t.view_count >= 3)
    .slice(0, 3)
    .map(t => t.ticker);

  return suggestions;
}

// Check if user uses a feature frequently
export function isFrequentFeatureUser(feature: string, threshold = 5): boolean {
  const profile = loadProfile();
  const usage = profile.most_used_features.find(f => f.feature === feature);
  return usage ? usage.use_count >= threshold : false;
}

// Get prioritized sectors for dashboard display
export function getPrioritizedSectors(): string[] {
  const profile = loadProfile();
  const defaultSectors = ['Technology', 'Financial Services', 'Healthcare', 'Energy', 'Consumer Cyclical'];

  if (profile.sectors_of_interest.length === 0) {
    return defaultSectors;
  }

  // Merge user interests with defaults, user interests first
  const merged = [...profile.sectors_of_interest];
  for (const s of defaultSectors) {
    if (!merged.includes(s)) merged.push(s);
  }

  return merged.slice(0, 5);
}

// Dismiss a suggestion
export function dismissSuggestion(suggestionId: string): void {
  const profile = loadProfile();
  if (!profile.dismissed_suggestions.includes(suggestionId)) {
    profile.dismissed_suggestions.push(suggestionId);
    saveProfile(profile);
  }
}

// Check if suggestion is dismissed
export function isSuggestionDismissed(suggestionId: string): boolean {
  const profile = loadProfile();
  return profile.dismissed_suggestions.includes(suggestionId);
}

// Earn an achievement
export function earnAchievement(achievementId: string): boolean {
  const profile = loadProfile();
  if (!profile.achievements_earned.includes(achievementId)) {
    profile.achievements_earned.push(achievementId);
    saveProfile(profile);
    return true; // New achievement earned
  }
  return false; // Already had it
}

// Check if achievement is earned
export function hasAchievement(achievementId: string): boolean {
  const profile = loadProfile();
  return profile.achievements_earned.includes(achievementId);
}

// Complete onboarding step
export function completeOnboardingStep(step: number): void {
  const profile = loadProfile();
  if (step > profile.onboarding_step) {
    profile.onboarding_step = step;
  }
  if (step >= 4) {
    profile.onboarding_completed = true;
  }
  saveProfile(profile);
}

// Check if user is first-time
export function isFirstTimeUser(): boolean {
  const profile = loadProfile();
  return profile.session_count <= 1 && !profile.onboarding_completed;
}

// Get time since last session in hours
export function getHoursSinceLastSession(): number {
  const profile = loadProfile();
  if (!profile.last_session_date) return 0;

  const lastSession = new Date(profile.last_session_date);
  const now = new Date();
  return (now.getTime() - lastSession.getTime()) / (1000 * 60 * 60);
}

// Check if should show return briefing
export function shouldShowReturnBriefing(): boolean {
  const hours = getHoursSinceLastSession();
  const profile = loadProfile();

  // Show if more than 8 hours and not first session
  return hours >= 8 && profile.session_count > 1;
}

// Export for use in React hooks
export const personalizationService = {
  loadProfile,
  saveProfile,
  startSession,
  endSession,
  trackTickerView,
  trackFeatureUsage,
  trackChartInterval,
  trackScreenerFilters,
  getPersonalizedGreeting,
  shouldSuggestWatchlist,
  getSuggestedTickers,
  isFrequentFeatureUser,
  getPrioritizedSectors,
  dismissSuggestion,
  isSuggestionDismissed,
  earnAchievement,
  hasAchievement,
  completeOnboardingStep,
  isFirstTimeUser,
  getHoursSinceLastSession,
  shouldShowReturnBriefing,
};

export default personalizationService;
